import { JSX, ReactNode, useCallback, useMemo, useState } from "react";

import { DialogContext } from "./DialogContext";
import { DialogContextValue, DialogState } from "./types";

export interface DialogProviderProps<TDialogNames extends string = string> {
  /** Child components that will have access to dialog context */
  children: ReactNode;
  /** Optional initial dialogs configuration */
  initialDialogs?: TDialogNames[];
}

/**
 * Provider component that manages dialog state for all dialogs in the application
 *
 * @example
 * ```tsx
 * type MyDialogNames = 'confirm' | 'edit' | 'delete';
 *
 * <DialogProvider<MyDialogNames> initialDialogs={['confirm', 'edit']}>
 *   <App />
 * </DialogProvider>
 * ```
 */
export const DialogProvider = <TDialogNames extends string = string>({
  children,
  initialDialogs = [],
}: DialogProviderProps<TDialogNames>): JSX.Element => {
  const [dialogs, setDialogs] = useState<DialogState<TDialogNames>[]>(
    initialDialogs.map((name) => ({
      name,
      isOpen: false,
    })),
  );

  const openDialog = useCallback(function <TData>(
    name: TDialogNames,
    data?: TData,
  ) {
    setDialogs((prev) => {
      const existing = prev.find((d) => d.name === name);
      if (existing) {
        return prev.map((d) =>
          d.name === name ? { ...d, isOpen: true, data } : d,
        );
      }
      return [...prev, { name, isOpen: true, data }];
    });
  }, []);

  const closeDialog = useCallback((name: TDialogNames) => {
    setDialogs((prev) => prev.filter((d) => d.name !== name));
  }, []);

  const getDialog = useCallback(
    function <TData>(
      name: TDialogNames,
    ): DialogState<TDialogNames, TData> | undefined {
      return dialogs.find((d) => d.name === name) as
        | DialogState<TDialogNames, TData>
        | undefined;
    },
    [dialogs],
  );

  const isDialogOpen = useCallback(
    (name: TDialogNames): boolean =>
      dialogs.find((d) => d.name === name)?.isOpen ?? false,
    [dialogs],
  );

  const value = useMemo<DialogContextValue<TDialogNames>>(
    () => ({
      dialogs,
      openDialog,
      closeDialog,
      getDialog,
      isDialogOpen,
    }),
    [dialogs, openDialog, closeDialog, getDialog, isDialogOpen],
  );

  return (
    <DialogContext.Provider value={value}>{children}</DialogContext.Provider>
  );
};

DialogProvider.displayName = "DialogProvider";
